import { Bindable, nameof, NotifyProperty } from "bindable-data";
import { Ticker } from "pixi.js";
import { GameState } from "./data/GameState";
import { injectable } from "@launchtray/tsyringe-async";
import { LevelModel } from "../../../common/models/LevelModel";
import { LoseType } from "../../popups/lose/LoseType";
import { Event } from "event-system";
import { TutorialModel } from "../../../common/models/TutorialModel";

@injectable()
export class GameModel extends Bindable {
    public readonly restoreVegetables: Event<number>;
    private eatenVegetables: number;
    public get savedVegetables(): number {
        return this.levelModel.levelConfiguration.vegetables - this.eatenVegetables;
    }
    public get isVegetablesGone(): boolean {
        return this.levelModel.levelConfiguration.vegetables != 0 && this.levelModel.levelConfiguration.vegetables <= this.eatenVegetables;
    }
    public get vegetablesForContinue(): number {
        return Math.max(Math.floor(this.levelModel.levelConfiguration.vegetables * 0.5), 1);
    }
    private enemiesOnField: number;
    private isCharacterAlive: boolean;
    public readonly time: NotifyProperty<number>;
    public readonly state: NotifyProperty<GameState>;

    public get totalLevelTimeMS(): number {
        return this.levelModel.levelConfiguration.time;
    }
    private timeToFirstMole: number;
    public readonly forceMoleSpawn: Event;

    public constructor(
        private readonly levelModel: LevelModel,
        private readonly tutorial: TutorialModel
    ) {
        super();
        this.state = new NotifyProperty<GameState>(this, nameof(this, "state"), GameState.Idle);
        this.time = new NotifyProperty<number>(this, nameof(this, "time"), -1);
        this.restoreVegetables = new Event();
        this.isCharacterAlive = false;
        this.timeToFirstMole = -1;
        this.forceMoleSpawn = new Event();
    }

    public handleEnemySpawn(): void {
        this.enemiesOnField++;
    }
    public handleEnemyDespawn(): void {
        this.enemiesOnField = Math.max(this.enemiesOnField - 1, 0);
    }

    public handleCharacterDie(): void {
        this.isCharacterAlive = false;
    }

    public handleCharacterResurrect(): void {
        this.time.setValue(this.time.value + 10000);
        this.isCharacterAlive = true;
    }

    public handleVegetableEaten(): void {
        this.eatenVegetables++;
    }

    public startLevel(): void {
        this.eatenVegetables = 0;
        this.enemiesOnField = 0;
        this.isCharacterAlive = true;
        this.time.setValue(this.levelModel.levelConfiguration.time);
        this.timeToFirstMole = this.levelModel.levelConfiguration.timeToFirstMole;
        this.state.setValue(GameState.Playing);
        Ticker.shared.add(this.onTick, this);
        Ticker.shared.add(this.onCountFirstMoleTime, this);
    }

    public endLevel(): void {
        this.eatenVegetables = 0;
        this.enemiesOnField = 0;
        this.state.setValue(GameState.Idle);
        Ticker.shared.remove(this.onTick, this);
        Ticker.shared.remove(this.onCountFirstMoleTime, this);
    }

    public continueLevel(type: LoseType): void {
        if (type === LoseType.NoVegetablesLeft) {
            this.eatenVegetables -= this.vegetablesForContinue;
            this.restoreVegetables.emit(this.vegetablesForContinue);
        }
        this.state.setValue(GameState.Playing);
        Ticker.shared.add(this.onTick, this);
    }

    private onTick(): void {
        if (!this.tutorial.moleShootTutorialComplete.value) {
            return;
        }
        this.time.setValue(Math.max(this.time.value - Ticker.shared.elapsedMS, 0));
        if (this.isVegetablesGone) {
            this.state.setValue(GameState.Lose);
            this.stopTicker();
            return;
        }
        if (!this.isCharacterAlive) {
            this.state.setValue(GameState.Lose);
            this.stopTicker();
            return;
        }
        if (this.time.value === 0 && this.enemiesOnField === 0) {
            this.state.setValue(GameState.Victory);
            this.stopTicker();
            return;
        }
    }

    private onCountFirstMoleTime(): void {
        this.timeToFirstMole = Math.max(this.timeToFirstMole - Ticker.shared.deltaMS, 0);
        if (this.timeToFirstMole === 0) {
            Ticker.shared.remove(this.onCountFirstMoleTime, this);
            this.forceMoleSpawn.emit();
        }
    }

    private stopTicker(): void {
        Ticker.shared.remove(this.onTick, this);
        Ticker.shared.remove(this.onCountFirstMoleTime, this);
    }

}